import _get from 'lodash/get';
import { PMCConfig } from '@kyruus/types';

import {
  SearchFacet,
  Term,
  SearchSummary,
  SearchFacetWithAppliedTerms
} from 'Src/types';

import messages from './messages';
import { DEFAULT_UNAPPLIED_CHIP_COUNT } from './constants';

const HIDDEN_FACETS = [
  'location',
  'locations.primary_marketable_location_id',
  'locations.associated_marketable_location_ids',
  'provider.id'
];

/**
 * Check if a facet should be shown
 * @param facet
 * @returns {boolean}
 */
export const shouldShowFacet = (facet: SearchFacet) =>
  !HIDDEN_FACETS.includes(facet.field) && !!facet?.terms?.length;

export const getFilterArray = (filter: string | string[] | undefined) => {
  if (!filter) return [];
  return Array.isArray(filter) ? filter : [filter];
};

export interface OrderedFacets {
  appliedCount: number;
  orderedFacets: SearchFacetWithAppliedTerms[];
}

/**
 * Get the message for the all filters chip based on the count of applied filters
 * @param countOfAppliedFilters
 * @returns
 */
export const getAllFiltersMessage = (countOfAppliedFilters: number) => {
  if (countOfAppliedFilters == 0) {
    return messages.allFiltersChipWithoutCount;
  }
  return messages.allFiltersChipWithCount;
};

/**
 * Get ordered facets with applied and unapplied terms, along with the count of
 * applied filters. The location facet is hidden from the facet list, but counts
 * as applied when a distance is set, since that's when it renders as an applied
 * chip.
 * @param config
 * @param facets
 * @param searchSummary
 * @returns {OrderedFacets}
 */
export const getOrderedFacets = (
  config: PMCConfig,
  facets: SearchFacet[],
  searchSummary: SearchSummary
): OrderedFacets => {
  const unappliedFacetLimit = _get(
    config,
    'search_results_page_v9.filterbar.chip.max_visible',
    DEFAULT_UNAPPLIED_CHIP_COUNT
  );
  // map facets to a key value pair for faster lookup
  const facetMap = (facets ?? []).reduce<{
    [key: string]: SearchFacetWithAppliedTerms;
  }>((acc, facet) => {
    acc[facet.field] = {
      ...facet,
      appliedTerms: facet.terms?.filter((term) => term.applied) || []
    };
    return acc;
  }, {});

  const locationFacet = facetMap['location'];

  const { applied, unapplied } = Object.values(facetMap)
    .filter((f) => shouldShowFacet(f))
    .reduce<{
      applied: SearchFacetWithAppliedTerms[];
      unapplied: SearchFacetWithAppliedTerms[];
    }>(
      (acc, facet) => {
        if (facet.appliedTerms.length > 0) {
          acc.applied.push(facet);
        } else {
          acc.unapplied.push({
            ...facet,
            appliedTerms: facet.terms.filter((term) => term.applied)
          });
        }
        return acc;
      },
      { applied: [], unapplied: [] }
    );

  const sortedApplied = searchSummary.filter
    ? applied.sort((a, b) => {
        const filterArray = getFilterArray(searchSummary.filter);
        const filterAIdx = filterArray.findIndex((f) => f?.includes(a.field));
        const filterBIdx = filterArray.findIndex((f) => f?.includes(b.field));

        if (filterAIdx > filterBIdx) {
          return 1;
        }

        if (filterBIdx > filterAIdx) {
          return -1;
        }

        return 0;
      })
    : applied;

  const limitedUnappliedFacets = unapplied.slice(0, unappliedFacetLimit);

  if (locationFacet && searchSummary.distance) {
    sortedApplied.unshift(locationFacet);
  } else if (locationFacet) {
    limitedUnappliedFacets.unshift(locationFacet);
  }

  const orderedFacets = [...sortedApplied, ...limitedUnappliedFacets];

  return {
    appliedCount: sortedApplied.length,
    orderedFacets
  };
};

/**
 * The no results search response returns facets without terms, so the applied
 * terms have to be reconstructed from `searchSummary.filter`.
 *
 * Returns the same shape as `getOrderedFacets`, with `terms` populated from the
 * applied filters, in the order the filters were applied. Facets with no applied
 * filters are omitted, since only applied chips are rendered on the no results
 * page - which also means every returned facet counts as applied.
 */
export const getOrderedFacetsForNoResults = (
  facets: SearchFacet[],
  searchSummary: SearchSummary
): OrderedFacets => {
  const { distance, filter } = searchSummary;
  if (!filter && !distance) {
    return { appliedCount: 0, orderedFacets: [] };
  }
  const facetsByField = new Map<string, SearchFacetWithAppliedTerms>();
  getFilterArray(filter).forEach((filterParam) => {
    const [field, value] = filterParam.split(':');
    const facet = facets.find((facet) => facet.field === field);
    if (!facet || HIDDEN_FACETS.includes(field)) return;

    const term: Term = {
      applied: true,
      count: 0,
      filter_param: filterParam,
      value
    };
    const existingFacet = facetsByField.get(field);
    if (existingFacet) {
      existingFacet.terms.push(term);
      existingFacet.appliedTerms.push(term);
    } else {
      facetsByField.set(field, {
        ...facet,
        terms: [term],
        appliedTerms: [term]
      });
    }
  });
  const orderedFacets = [...facetsByField.values()];
  const locationFacet = facets.find((facet) => facet.field === 'location');
  if (locationFacet && distance) {
    orderedFacets.unshift({
      ...locationFacet,
      terms: [],
      appliedTerms: []
    });
  }
  return { appliedCount: orderedFacets.length, orderedFacets };
};
